import RevaText from "components/common/RevaText";
import { useCallback, useContext, useEffect, useState } from "react";
import UserContext from "context/UserContext";
import { FormattedMessage, useIntl } from "react-intl";
import { DatePicker } from "@mui/x-date-pickers";
import sports from "constants/sports";
import performRequest from "network/perform-request";
import {
  categoriesForTournamentsRoute,
  classificationsIndexRoute,
  deleteTournamentRoute,
  punctuationIndexRoute,
  restartTournamentRoute,
  tournamentsCreateRoute,
  tournamentsUpdateRoute,
  leagueCustomerTournamentsRoute,
} from "network/api-routes";
import CategoryAutocomplete from "./CategoryAutocomplete";
import {
  AMERICANO,
  DOUBLE_ELIMINATION,
  FOUR_PLAYER_TWO_ROUNDS,
  GROUP_STAGE_ONLY,
  ROUND_ROBIN,
  SECOND_ROUND_CROSS_RESULT,
  SECOND_ROUND_SAME_RESULT,
  SINGLE_ELIMINATION,
  LEAGUE,
} from "constants/tournamentTypes";
import LeagueTournamentWizardFields from "./LeagueTournamentWizardFields";
import CurrencySelector from "components/common/CurrencySelector";
import { useParams, useHistory } from "react-router-dom";
import { useCompetitionAdminRoutes } from "hooks/useCompetitionAdminRoutes";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { buildCompetitionAdminRoutes } from "constants/competition-admin-routes";
import CTAButton from "components/common/CTAButton";
import dayjs from "dayjs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AlertDialog from "components/common/AlertDialog";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useSnackbar } from "notistack";
import { TOURNAMENT_PENDING } from "constants/tournamentStates";

const {
  Alert,
  Box,
  Paper,
  Autocomplete,
  TextField,
  Stack,
  Tooltip,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  FormHelperText,
  OutlinedInput,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Grid2,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} = require("@mui/material");

const FORMAT_BRACKET_ONLY = "BRACKET_ONLY";
const FORMAT_GROUP_ONLY = "GROUP_ONLY";
const FORMAT_GROUP_PLUS_BRACKET = "GROUP_PLUS_BRACKET";
const FORMAT_AMERICANO = "AMERICANO";
const FORMAT_DOUBLE_ELIMINATION_BRACKET = "DOUBLE_ELIMINATION_BRACKET";
const FORMAT_LEAGUE = "LEAGUE";

const SectionContainer = ({ title, children }) => (
  <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
    {title && <RevaText id={title} variant="h4" mb={4} />}
    {children}
  </Paper>
);

function CreateTournamentWizard({ tournament, competition, organization = null, onSubmitResponse = null }) {
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { id, customerId: routeCustomerId } = useParams();
  const history = useHistory();
  const competitionUuid = competition?.uuid_ || competition?.data?.uuid_ || id;
  const routes = useCompetitionAdminRoutes(competitionUuid);
  const gestionCustomerId = routeCustomerId || routes.customerId;
  const tournamentHasStarted = tournament && tournament?.state !== TOURNAMENT_PENDING;
  const { enqueueSnackbar } = useSnackbar();
  const [categories, setCategories] = useState([]);
  const [categoryPolicy, setCategoryPolicy] = useState({ custom_category_allowed: true, official_category_required: false });
  const selectedOrganization =
    organization || user.organizations?.find((org) => org.id === tournament?.competition?.data?.organization_id);
  const [classifications, setClassifications] = useState([]);
  const [selectedClassification, setSelectedClassification] = useState(null);
  const [punctuationSystems, setPunctuationSystems] = useState([]);
  const [selectedPunctuationSystem, setSelectedPunctuationSystem] = useState(null);
  const [selectedSport, setSport] = useState(tournament?.sport);
  const [selectedCategory, setCategory] = useState(tournament?.category?.data);
  const [selectedCategories, setSelectedCategories] = useState(tournament?.interclubCategories ?? []);
  const [startDate, setStartDate] = useState(tournament ? dayjs(tournament.start_at) : null);
  const [restartDialogOpen, setRestartDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [formData, setFormData] = useState(
    Object.assign(
      {
        competition_id: competitionUuid,
        // General information
        name: "",
        description: "",
        sport_slug: null,
        category_id: null,
        start_at: null,
        // Tournament format
        has_group_stage: false,
        tournament_type: SINGLE_ELIMINATION,
        modality: "",
        players_per_team: null,
        players_per_group: "",
        second_round_pairing: null,
        advancing_players_per_group: "",
        advance_participants_when_group_stage_is_over: false,
        default_sets: 1,
        is_home_and_away: false,
        enforce_min_participants_per_group: false,
        perfect_bracket: false,
        // Ranking & Classification
        classification_id: null,
        punctuation_system_id: null,
        use_ranking_system: false,
        // Rules & Participants
        signup_cap: null,
        allow_self_reporting: false,
        requires_document_for_players: false,
        // Visibility & Registration
        signup_price: null,
        currency_code: competition?.currency_code ?? tournament?.currency_code ?? "",
        show_bracket: true,
        open_signup: true,
        is_interclub: false,
        interclub_categories: (tournament?.interclubCategories ?? []).map((cat) => cat.id),
        year: tournament?.year ?? new Date().getFullYear(),
        copy_lineup_from_tournament_id: null,
        generate_fixtures: false,
        matchdays_count: null,
        league_setup_mode: "manual",
        groups_enabled: true,
        groups_count: 1,
        teams_per_group: 4,
        advance_per_group: 0,
        group_home_and_away: false,
        knockout_enabled: false,
        knockout_home_and_away: false,
        knockout_final_home_and_away: false,
      },
      tournament ?? {},
      { currency_code: competition?.currency_code ?? tournament?.currency_code ?? "" },
    ),
  );
  const [tournamentFormat, setTournamentFormat] = useState(() => getTournamentFormatFromData(formData));
  const [leagueTournaments, setLeagueTournaments] = useState([]);
  const customerId = competition?.customer_id ?? competition?.data?.customer_id ?? null;
  const isLeagueFormat = tournamentFormat === FORMAT_LEAGUE;
  const requiresPunctuationSystem = Boolean(formData.classification_id) && !isLeagueFormat;
  const shouldShowPunctuationSystemError =
    hasAttemptedSubmit && requiresPunctuationSystem && !formData.punctuation_system_id;

  const stepTitles = [
    "Información general",
    "Formato del torneo",
    "Ranking y Clasificación",
    "Reglas y Participantes",
    "Visibilidad y Registro",
  ];

  function getTournamentFormatFromData(data) {
    const { has_group_stage, group_stage_type, tournament_type } = data;

    if (tournament_type === LEAGUE) {
      return FORMAT_LEAGUE;
    }

    if (!has_group_stage && tournament_type === SINGLE_ELIMINATION) {
      return FORMAT_BRACKET_ONLY;
    }

    if (
      has_group_stage &&
      [ROUND_ROBIN, FOUR_PLAYER_TWO_ROUNDS].includes(group_stage_type) &&
      tournament_type === SINGLE_ELIMINATION
    ) {
      return FORMAT_GROUP_PLUS_BRACKET;
    }

    if (
      has_group_stage &&
      [ROUND_ROBIN, FOUR_PLAYER_TWO_ROUNDS].includes(group_stage_type) &&
      tournament_type === GROUP_STAGE_ONLY
    ) {
      return FORMAT_GROUP_ONLY;
    }

    if (has_group_stage && group_stage_type === AMERICANO && tournament_type === GROUP_STAGE_ONLY) {
      return FORMAT_AMERICANO;
    }

    if (!has_group_stage && tournament_type === DOUBLE_ELIMINATION) {
      return FORMAT_DOUBLE_ELIMINATION_BRACKET;
    }

    return null;
  }

  const handleSetTournamentFormat = (format) => {
    setTournamentFormat(format);

    let updatedData = {
      advancing_players_per_group: null,
      players_per_group: null,
      second_round_pairing: null,
      perfect_bracket: false,
      enforce_min_participants_per_group: false,
    };

    switch (format) {
      case FORMAT_GROUP_ONLY:
        updatedData = {
          ...updatedData,
          has_group_stage: true,
          group_stage_type: ROUND_ROBIN,
          tournament_type: GROUP_STAGE_ONLY,
        };
        break;

      case FORMAT_GROUP_PLUS_BRACKET:
        updatedData = {
          ...updatedData,
          has_group_stage: true,
          group_stage_type: ROUND_ROBIN,
          tournament_type: SINGLE_ELIMINATION,
        };
        break;

      case FORMAT_AMERICANO:
        updatedData = {
          ...updatedData,
          has_group_stage: true,
          group_stage_type: AMERICANO,
          tournament_type: GROUP_STAGE_ONLY,
          modality: "singles",
        };
        break;

      case FORMAT_DOUBLE_ELIMINATION_BRACKET:
        updatedData = {
          ...updatedData,
          has_group_stage: false,
          group_stage_type: null,
          tournament_type: DOUBLE_ELIMINATION,
        };
        break;

      case FORMAT_LEAGUE: {
        const soccer = sports.find((sport) => sport.slug === "soccer");
        if (soccer) {
          setSport(soccer);
          getCategories(soccer.slug);
        }
        updatedData = {
          ...updatedData,
          tournament_type: LEAGUE,
          has_group_stage: true,
          group_stage_type: ROUND_ROBIN,
          modality: "teams",
          players_per_team: 22,
          is_home_and_away: false,
          group_home_and_away: false,
          year: new Date().getFullYear(),
          league_setup_mode: "manual",
          groups_enabled: true,
          groups_count: 1,
          teams_per_group: 4,
          advance_per_group: 0,
          knockout_enabled: false,
          knockout_home_and_away: false,
          knockout_final_home_and_away: false,
          generate_fixtures: false,
          sport_slug: "soccer",
          show_bracket: false,
          open_signup: false,
          use_ranking_system: false,
          perfect_bracket: false,
          signup_cap: null,
        };
        break;
      }

      case FORMAT_BRACKET_ONLY:
      default:
        updatedData = {
          ...updatedData,
          has_group_stage: false,
          group_stage_type: null,
          tournament_type: SINGLE_ELIMINATION,
        };
        break;
    }

    setFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));
  };

  const getPunctuationSystems = useCallback(
    (organizationID) => {
      performRequest("POST", punctuationIndexRoute, { organization_id: organizationID }, true, user)
        .then((response) => {
          const punctuationSystems = response.data;
          setPunctuationSystems(punctuationSystems);
          if (tournament) {
            const selectedPunctuationSystem = punctuationSystems.find(
              (ps) => ps.id === Number(tournament?.punctuation_system_id),
            );
            setSelectedPunctuationSystem(selectedPunctuationSystem);
            setFormData((prev) => ({
              ...prev,
              punctuation_system_id: selectedPunctuationSystem?.id,
            }));
          }
        })
        .catch((error) => {
          // this.setState({ punctuationSystemsReady: true });
          console.log(error);
        });
    },
    [user, tournament],
  );

  const getClassifications = useCallback(
    (orgID) => {
      performRequest("POST", classificationsIndexRoute, { organization_id: orgID }, true, user)
        .then((response) => {
          setClassifications(response.data);
          if (formData.classification_id) {
            const classification = response.data.find((c) => c.id === Number(formData.classification_id));

            if (classification) {
              setSelectedClassification(classification);
              getPunctuationSystems(orgID);
            }
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [user, formData.classification_id, getPunctuationSystems],
  );

  const getCategories = useCallback(
    (sportSlug) => {
      const country = competition?.country || tournament?.competition?.data?.country;
      performRequest(
        "POST",
        categoriesForTournamentsRoute,
        { sport_slug: sportSlug, country: country, organization_id: selectedOrganization?.id },
        true,
        user,
      )
        .then((response) => {
          const categories = response.data.data;
          setCategoryPolicy(response.data.meta || { custom_category_allowed: true, official_category_required: false });
          const sportChanged = selectedSport?.slug !== sportSlug;
          setCategories(categories);

          if (sportChanged) {
            console.log("category_id set to null after index of categories");
            setCategory(null);
          }

          setFormData((prev) => ({
            ...prev,
            sport_slug: sportSlug,
            category_id: sportChanged ? null : prev.category_id,
            new_category_name: null,
            new_category_modality: null,
          }));
        })
        .catch((error) => {
          enqueueSnackbar(
            error?.response?.data?.message || intl.formatMessage({ id: "Lo sentimos, ha ocurrido un error" }),
            {
              variant: "error",
            },
          );
        });
    },
    [enqueueSnackbar, intl, user, selectedSport?.slug, selectedOrganization?.id, competition, tournament],
  );

  useEffect(() => {
    if (selectedOrganization) {
      getClassifications(selectedOrganization.id);
    }
  }, [selectedOrganization, getClassifications]);

  useEffect(() => {
    if (formData.sport_slug) {
      getCategories(formData.sport_slug);
    }
  }, [getCategories, formData.sport_slug]);

  useEffect(() => {
    if (!customerId || !isLeagueFormat || tournament) {
      return;
    }
    performRequest("GET", leagueCustomerTournamentsRoute(customerId), { page_size: 100 }, true, user)
      .then((response) => {
        const results = Array.isArray(response.data?.results) ? response.data.results : [];
        setLeagueTournaments(results.filter((item) => !item.archived));
      })
      .catch(() => setLeagueTournaments([]));
  }, [customerId, isLeagueFormat, tournament, user]);

  const createTournament = (e) => {
    e.preventDefault();

    setHasAttemptedSubmit(true);

    if (requiresPunctuationSystem && !formData.punctuation_system_id) {
      enqueueSnackbar(
        intl.formatMessage({
          id: "Debes seleccionar un sistema de puntuación",
        }),
        { variant: "warning" },
      );
      return;
    }

    const headers = { "Content-Type": "multipart/form-data" };
    const formDataToSend = new FormData();

    setSubmitting(true);

    // Recorrer cada propiedad del objeto y agregarla a FormData
    Object.keys(formData).forEach((key) => {
      let value = formData[key];
      if (value === undefined) {
        value = null;
      }
      formDataToSend.append(key, value === null ? "" : value);
    });

    formDataToSend.delete("currency_code");

    if (tournament) {
      formDataToSend.append("tournament_id", tournament.id);
      // This form only carries creation defaults for the final-phase settings. The backend rebuilds (or removes) the
      // bracket when they change, so an edit must never send them: Gestión's Configuración tab is where they change.
      ["knockout_enabled", "advance_per_group", "knockout_home_and_away", "knockout_final_home_and_away"].forEach((key) =>
        formDataToSend.delete(key),
      );
    }

    performRequest(
      "POST",
      tournament ? tournamentsUpdateRoute : tournamentsCreateRoute,
      formDataToSend,
      true,
      user,
      null,
      headers,
    )
      .then((response) => {
        setSubmitting(false);
        if (tournament) {
          enqueueSnackbar(
            intl.formatMessage({
              id: "Los cambios se guardaron correctamente",
            }),
            { variant: "success" },
          );
          if (onSubmitResponse) {
            onSubmitResponse(response.data.data);
          }
        } else {
          enqueueSnackbar(intl.formatMessage({ id: "Torneo creado con éxito" }), { variant: "success" });
          const createdUuid = response.data.data?.uuid_;
          if (gestionCustomerId && createdUuid) {
            history.push(GESTION_ROUTES.tournament(gestionCustomerId, createdUuid));
          } else {
            history.push(routes.tournaments);
          }
        }
      })
      .catch((error) => {
        setSubmitting(false);
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "Lo sentimos, ha ocurrido un error" }),
          {
            variant: "error",
          },
        );
      });
  };

  const resetTournament = () => {
    performRequest("POST", restartTournamentRoute, { tournament_id: tournament.id }, true, user)
      .then((response) => {
        onSubmitResponse(false);
        enqueueSnackbar(intl.formatMessage({ id: "Torneo reiniciado con éxito" }), { variant: "success" });
      })
      .catch((error) => {
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "Lo sentimos, ha ocurrido un error" }),
          {
            variant: "error",
          },
        );
      });
  };

  const confirmDeleteTournament = () => {
    const competitionUUID = tournament.competition?.data?.uuid_;
    performRequest("POST", deleteTournamentRoute, { tournament_id: tournament.id }, true, user)
      .then((response) => {
        enqueueSnackbar(intl.formatMessage({ id: "Torneo eliminado correctamente" }), { variant: "success" });
        if (gestionCustomerId) {
          history.push(GESTION_ROUTES.tournaments(gestionCustomerId));
        } else if (competitionUUID) {
          history.push(buildCompetitionAdminRoutes(competitionUUID, routes.customerId).tournaments);
        } else {
          history.push("/");
        }
      })
      .catch((error) => {
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "Lo sentimos, ha ocurrido un error" }),
          {
            variant: "error",
          },
        );
      });
  };

  return (
    <form onSubmit={createTournament}>
      <Stack sx={{ gap: 3 }}>
        {!tournament && <RevaText id="Nuevo torneo" variant="h2" textAlign="center" />}
        <SectionContainer title={stepTitles[0]}>
          <Stack gap={3}>
            <TextField
              required
              fullWidth
              value={formData.name}
              label={intl.formatMessage({ id: "Nombre del torneo" })}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              rows={4}
              fullWidth
              multiline
              label={intl.formatMessage({ id: "Descripción" })}
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
            <Stack
              sx={{
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "flex-start", md: "center" },
                gap: 3,
              }}
            >
              <Autocomplete
                fullWidth
                value={selectedSport}
                options={sports}
                disabled={isLeagueFormat}
                getOptionLabel={(sport) => intl.formatMessage({ id: sport.name })}
                onChange={(event, value) => {
                  setSport(value);
                  if (value) {
                    getCategories(value.slug);
                  } else {
                    setCategory(null);
                    setCategories([]);
                    setFormData((prev) => ({
                      ...prev,
                      sport_slug: null,
                      category_id: null,
                      new_category_name: null,
                      new_category_modality: null,
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label={intl.formatMessage({
                      id: "Seleccione el deporte del torneo",
                    })}
                  />
                )}
              />
              <Tooltip
                followCursor
                title={
                  !selectedSport
                    ? intl.formatMessage({
                        id: "Debes seleccionar un deporte primero",
                      })
                    : ""
                }
              >
                <CategoryAutocomplete
                  categories={categories}
                  disabled={!selectedSport}
                  selectedCategory={selectedCategory}
                  customCategoryAllowed={categoryPolicy.custom_category_allowed}
                  officialCategoryRequired={categoryPolicy.official_category_required}
                  onCategorySelected={(category) => {
                    setCategory(category);
                    setSelectedPunctuationSystem(null); // Limpiar visualmente la selección anterior

                    if (category?.id) {
                      const matchedClassification = classifications.find(
                        (c) => Number(c.category_id) === Number(category.id),
                      );

                      if (matchedClassification) {
                        setSelectedClassification(matchedClassification);
                      } else {
                        setSelectedClassification(null);
                        setSelectedPunctuationSystem(null);
                      }

                      setFormData((prev) => ({
                        ...prev,
                        category_id: category.id,
                        classification_id: matchedClassification ? matchedClassification.id : null,
                        punctuation_system_id: null,
                        use_ranking_system: false, // Se apaga hasta que elija puntuación
                        new_category_name: null,
                        new_category_modality: null,
                      }));
                    } else {
                      setSelectedClassification(null);
                      setSelectedPunctuationSystem(null);
                      setFormData((prev) => ({
                        ...prev,
                        category_id: null,
                        classification_id: null,
                        punctuation_system_id: null,
                        use_ranking_system: false,
                        new_category_name: category?.name || null,
                        new_category_modality: category?.modality || null,
                      }));
                    }
                  }}
                />
              </Tooltip>
              <DatePicker
                sx={{ width: "100%" }}
                label={intl.formatMessage({ id: "Fecha de inicio" })}
                value={startDate}
                onChange={(date) => {
                  setStartDate(date);
                  setFormData((prev) => ({
                    ...prev,
                    start_at: dayjs(date).format("YYYY-MM-DD HH:mm:ss"),
                  }));
                }}
                slotProps={{ textField: { required: true } }}
                minDate={null}
                maxDate={formData.endDate}
              />
            </Stack>
          </Stack>
        </SectionContainer>

        <SectionContainer title={stepTitles[1]}>
          <Stack gap={3}>
            <FormControl required fullWidth>
              <InputLabel id="tournamentType">{intl.formatMessage({ id: "Formato del torneo" })}</InputLabel>
              <Select
                label={intl.formatMessage({ id: "Formato del torneo" })}
                value={tournamentFormat}
                onChange={(e) => handleSetTournamentFormat(e.target.value)}
                disabled={tournamentHasStarted}
              >
                <MenuItem value={FORMAT_BRACKET_ONLY}>
                  {intl.formatMessage({ id: "Llave eliminatoria directa" })}
                </MenuItem>
                {/* <MenuItem value={FORMAT_DOUBLE_ELIMINATION_BRACKET}>
                  {intl.formatMessage({
                    id: "Llave eliminatoria doble (sin fase de grupos)",
                  })}
                </MenuItem> */}
                <MenuItem value={FORMAT_GROUP_ONLY}>
                  {intl.formatMessage({
                    id: "Fase de grupos (sin llave final)",
                  })}
                </MenuItem>
                <MenuItem value={FORMAT_GROUP_PLUS_BRACKET}>
                  {intl.formatMessage({
                    id: "Fase de grupos + Llave eliminatoria final",
                  })}
                </MenuItem>
                {formData.sport_slug === "paddle" && (
                  <MenuItem value={FORMAT_AMERICANO}>
                    {intl.formatMessage({
                      id: "Americano (Todos contra Todos)",
                    })}
                  </MenuItem>
                )}
                <MenuItem value={FORMAT_LEAGUE}>{intl.formatMessage({ id: "league.wizard.formatLabel" })}</MenuItem>
              </Select>
            </FormControl>
            {isLeagueFormat ? (
              <LeagueTournamentWizardFields
                formData={formData}
                setFormData={setFormData}
                leagueTournaments={leagueTournaments}
                tournamentHasStarted={tournamentHasStarted}
                isEdit={Boolean(tournament)}
              />
            ) : null}
            {!isLeagueFormat && formData.has_group_stage && formData.group_stage_type !== AMERICANO && (
              <Stack gap={2}>
                <FormControl required fullWidth>
                  <InputLabel id="group-stage-format">
                    {intl.formatMessage({ id: "Formato de los partidos de grupo" })}
                  </InputLabel>
                  <Select
                    labelId="group-stage-format"
                    label={intl.formatMessage({ id: "Formato de los partidos de grupo" })}
                    value={formData.group_stage_type || ROUND_ROBIN}
                    disabled={tournamentHasStarted || formData.is_interclub}
                    onChange={(event) => {
                      const groupStageType = event.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        group_stage_type: groupStageType,
                        players_per_group:
                          groupStageType === FOUR_PLAYER_TWO_ROUNDS ? 3 : prev.players_per_group,
                        enforce_min_participants_per_group:
                          groupStageType === FOUR_PLAYER_TWO_ROUNDS
                            ? true
                            : prev.enforce_min_participants_per_group,
                        second_round_pairing:
                          groupStageType === FOUR_PLAYER_TWO_ROUNDS
                            ? prev.second_round_pairing || SECOND_ROUND_CROSS_RESULT
                            : null,
                        is_home_and_away:
                          groupStageType === FOUR_PLAYER_TWO_ROUNDS ? false : prev.is_home_and_away,
                        is_interclub: groupStageType === FOUR_PLAYER_TWO_ROUNDS ? false : prev.is_interclub,
                      }));
                    }}
                  >
                    <MenuItem value={ROUND_ROBIN}>{intl.formatMessage({ id: "Todos contra todos" })}</MenuItem>
                    <MenuItem value={FOUR_PLAYER_TWO_ROUNDS}>
                      {intl.formatMessage({ id: "Grupos de 3; grupos de 4 en 2 rondas" })}
                    </MenuItem>
                  </Select>
                </FormControl>
                {formData.group_stage_type === FOUR_PLAYER_TWO_ROUNDS && (
                  <>
                    <FormControl required fullWidth>
                      <InputLabel id="second-round-pairing">
                        {intl.formatMessage({ id: "Cruces de la segunda ronda" })}
                      </InputLabel>
                      <Select
                        labelId="second-round-pairing"
                        label={intl.formatMessage({ id: "Cruces de la segunda ronda" })}
                        value={formData.second_round_pairing || SECOND_ROUND_CROSS_RESULT}
                        disabled={tournamentHasStarted}
                        onChange={(event) =>
                          setFormData((prev) => ({ ...prev, second_round_pairing: event.target.value }))
                        }
                      >
                        <MenuItem value={SECOND_ROUND_CROSS_RESULT}>
                          {intl.formatMessage({ id: "Cruzados: ganador vs. perdedor" })}
                        </MenuItem>
                        <MenuItem value={SECOND_ROUND_SAME_RESULT}>
                          {intl.formatMessage({ id: "Mismo resultado: ganadores y perdedores" })}
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <RevaText color="text.secondary" id="groupStage.twoRounds.description" />
                  </>
                )}
              </Stack>
            )}
            {!isLeagueFormat && formData.has_group_stage && formData.tournament_type !== GROUP_STAGE_ONLY && (
              <Grid2 container spacing={3}>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    required
                    fullWidth
                    type="number"
                    label={intl.formatMessage({
                      id: "Participantes por grupo",
                    })}
                    value={formData.players_per_group}
                    disabled={tournamentHasStarted || formData.group_stage_type === FOUR_PLAYER_TWO_ROUNDS}
                    id="players-per-group"
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        players_per_group: event.target.value,
                      }))
                    }
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    required
                    fullWidth
                    type="number"
                    label={intl.formatMessage({
                      id: "¿Cuántos avanzan por grupo?",
                    })}
                    value={formData.advancing_players_per_group}
                    disabled={tournamentHasStarted}
                    id="advancing-players-per-group"
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        advancing_players_per_group: event.target.value,
                      }))
                    }
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormControl required fullWidth>
                    <InputLabel id="advance-participants-when-group-stage-is-over">
                      {intl.formatMessage({
                        id: "¿Cuándo avanzan los participantes a la fase final?",
                      })}
                    </InputLabel>
                    <Select
                      label={intl.formatMessage({
                        id: "¿Cuándo avanzan los participantes a la fase final?",
                      })}
                      labelId="advance-participants-when-group-stage-is-over"
                      value={formData.advance_participants_when_group_stage_is_over}
                      disabled={tournamentHasStarted}
                      onChange={(event) => {
                        const advance_participants_when_group_stage_is_over = event.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          advance_participants_when_group_stage_is_over,
                        }));
                      }}
                    >
                      <MenuItem value={true}>
                        {intl.formatMessage({
                          id: "Avanzar todos juntos al finalizar la fase de grupos",
                        })}
                      </MenuItem>
                      <MenuItem value={false}>
                        {intl.formatMessage({
                          id: "Avanzar participantes en cuanto terminen sus partidos",
                        })}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid2>
              </Grid2>
            )}
            {!isLeagueFormat ? (
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 6, md: 4 }}>
                <FormControl required fullWidth>
                  <InputLabel id="modality">{intl.formatMessage({ id: "Tipo de inscripción" })}</InputLabel>
                  <Select
                    label={intl.formatMessage({ id: "Tipo de inscripción" })}
                    labelId="modality"
                    value={formData.modality}
                    disabled={formData.group_stage_type === "americano" || tournamentHasStarted}
                    onChange={(event) => {
                      const modality = event.target.value;
                      if (modality !== "teams") {
                        setFormData((prev) => ({
                          ...prev,
                          modality,
                          players_per_team: null,
                        }));
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          modality,
                          players_per_team: 10,
                        }));
                      }
                    }}
                  >
                    <MenuItem value="singles">{intl.formatMessage({ id: "Individual (1 vs 1)" })}</MenuItem>
                    <MenuItem value="doubles">{intl.formatMessage({ id: "En parejas (2 vs 2)" })}</MenuItem>
                    <MenuItem value="teams">{intl.formatMessage({ id: "Por equipos" })}</MenuItem>
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{ xs: 6, md: 4 }}>
                {formData.modality === "teams" && (
                  <>
                    <FormControl>
                      <OutlinedInput
                        type="number"
                        required
                        defaultValue={10}
                        disabled={tournamentHasStarted}
                        value={formData.players_per_team}
                        id="players-per-team"
                        endAdornment={
                          <InputAdornment position="end">
                            {intl.formatMessage({ id: "jugadores por equipo" })}
                          </InputAdornment>
                        }
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            players_per_team: event.target.value,
                          }))
                        }
                        aria-describedby="outlined-weight-helper-text"
                        inputProps={{ "aria-label": "players-per-team" }}
                      />
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.is_interclub}
                          disabled={tournamentHasStarted || formData.group_stage_type === FOUR_PLAYER_TWO_ROUNDS}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              is_interclub: e.target.checked,
                              perfect_bracket: false,
                              is_home_and_away: false,
                            })
                          }
                        />
                      }
                      label="¿Este torneo es tipo Interclubes?"
                    />
                    {formData.is_interclub && (
                      <CategoryAutocomplete
                        multiple
                        categories={categories}
                        disabled={!selectedSport || tournamentHasStarted}
                        selectedCategory={selectedCategories} // un array
                        customCategoryAllowed={categoryPolicy.custom_category_allowed}
                        officialCategoryRequired={categoryPolicy.official_category_required}
                        onCategorySelected={(newCategories) => {
                          setSelectedCategories(newCategories);
                          setFormData((prev) => ({
                            ...prev,
                            interclub_categories: newCategories.map((cat) => cat.id).filter(Boolean),
                            // new_categories: newCategories
                            //   .filter((cat) => !cat.id)
                            //   .map((cat) => ({ name: cat.name, modality: cat.modality })),
                          }));
                        }}
                      />
                    )}
                  </>
                )}
              </Grid2>
              <Grid2 size={{ xs: 6, md: 4 }}>
                <FormControl>
                  <OutlinedInput
                    type="number"
                    value={formData.default_sets}
                    id="sets-per-match"
                    endAdornment={
                      <InputAdornment position="end">{intl.formatMessage({ id: "sets por partido" })}</InputAdornment>
                    }
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        default_sets: event.target.value,
                      }))
                    }
                    aria-describedby="outlined-weight-helper-text"
                    inputProps={{ "aria-label": "sets" }}
                  />
                  <FormHelperText id="outlined-weight-helper-text">
                    {intl.formatMessage({
                      id: "Se pueden agregar más sets durante el torneo.",
                    })}
                  </FormHelperText>
                </FormControl>
              </Grid2>
            </Grid2>
            ) : null}
            {!isLeagueFormat && formData.has_group_stage && formData.tournament_type !== GROUP_STAGE_ONLY && (
              <Stack sx={{ mt: 1, gap: 2 }}>
                <RevaText variant="h6" id="Opciones adicionales para fase de grupos" />
                <FormControl>
                  <FormControlLabel
                    value={formData.is_home_and_away}
                    control={
                      <Checkbox
                        disabled={
                          tournamentHasStarted ||
                          formData.is_interclub ||
                          formData.group_stage_type === FOUR_PLAYER_TWO_ROUNDS
                        }
                        checked={formData.is_home_and_away}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            is_home_and_away: event.target.checked,
                          }))
                        }
                      />
                    }
                    label={intl.formatMessage({ id: "Ida y vuelta" })}
                  />
                  <RevaText
                    color="text.secondary"
                    id="Activa esta opción si querés que cada cruce en la fase de grupos tenga un partido de ida y otro de vuelta."
                  />
                </FormControl>
                <FormControl>
                  <FormControlLabel
                    value={formData.enforce_min_participants_per_group}
                    control={
                      <Checkbox
                        disabled={tournamentHasStarted || formData.group_stage_type === FOUR_PLAYER_TWO_ROUNDS}
                        checked={formData.enforce_min_participants_per_group}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            enforce_min_participants_per_group: event.target.checked,
                          }))
                        }
                      />
                    }
                    label={intl.formatMessage({
                      id: "Evitar grupos incompletos",
                    })}
                  />
                  <RevaText color="text.secondary" id="No permite grupos con menos participantes de lo definido." />
                </FormControl>
                <FormControl>
                  <FormControlLabel
                    value={formData.perfect_bracket}
                    control={
                      <Checkbox
                        disabled={tournamentHasStarted || formData.is_interclub}
                        checked={formData.perfect_bracket}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            perfect_bracket: event.target.checked,
                          }))
                        }
                      />
                    }
                    label={intl.formatMessage({ id: "Llave perfecta" })}
                  />
                  <RevaText
                    color="text.secondary"
                    id="La llave final será una potencia de 2. Si faltan clasificados, avanzarán los mejores jugadores restantes."
                  />
                </FormControl>
              </Stack>
            )}
          </Stack>
        </SectionContainer>

        {selectedOrganization && !isLeagueFormat && (
          <SectionContainer title={stepTitles[2]}>
            <Stack gap={3}>
              <TextField
                fullWidth
                value={selectedOrganization.name}
                label={intl.formatMessage({ id: "Organización" })}
                disabled={true}
              />
              <Autocomplete
                fullWidth
                title={intl.formatMessage({ id: "Ranking" })}
                value={selectedClassification}
                options={classifications}
                disabled={!classifications.length}
                getOptionLabel={(classification) => classification.name}
                onChange={(event, value) => {
                  setSelectedClassification(value);
                  setSelectedPunctuationSystem(null);
                  setFormData((prev) => ({
                    ...prev,
                    classification_id: value?.id || null,
                    punctuation_system_id: null,
                    use_ranking_system: false, // Se apaga hasta que elija puntuación
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={intl.formatMessage({ id: "Ranking" })}
                    placeholder={intl.formatMessage({
                      id: "Selecciona un ranking de la organización seleccionada",
                    })}
                  />
                )}
              />
              <Autocomplete
                fullWidth
                title={intl.formatMessage({ id: "Sistema de puntuación" })}
                value={selectedPunctuationSystem}
                options={punctuationSystems}
                disabled={!punctuationSystems.length}
                getOptionLabel={(ps) => ps.name}
                onChange={(event, value) => {
                  setSelectedPunctuationSystem(value);
                  setFormData((prev) => ({
                    ...prev,
                    punctuation_system_id: value?.id || null,
                    use_ranking_system: !!(selectedClassification && value),
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={shouldShowPunctuationSystemError}
                    helperText={
                      shouldShowPunctuationSystemError
                        ? intl.formatMessage({ id: "Este campo es obligatorio para el ranking" })
                        : ""
                    }
                    label={intl.formatMessage({ id: "Sistema de puntuación" })}
                    placeholder={intl.formatMessage({
                      id: "Selecciona un sistema de puntuación para el torneo",
                    })}
                  />
                )}
              />
            </Stack>
          </SectionContainer>
        )}

        <SectionContainer title={stepTitles[3]}>
          <Stack gap={3}>
            {!isLeagueFormat ? (
            <FormControl>
              <TextField
                required
                value={formData.signup_cap}
                sx={{ maxWidth: 320 }}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    signup_cap: e.target.value,
                  }))
                }
                label={intl.formatMessage({
                  id: "Cantidad máxima de participantes",
                })}
              />
            </FormControl>
            ) : (
              <RevaText color="text.secondary">{intl.formatMessage({ id: "league.wizard.teamsFromRosterHint" })}</RevaText>
            )}
            {!isLeagueFormat ? (
            <FormControl>
              <FormControlLabel
                value={formData.allow_self_reporting}
                control={
                  <Checkbox
                    checked={formData.allow_self_reporting}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        allow_self_reporting: event.target.checked,
                      }))
                    }
                  />
                }
                label={intl.formatMessage({
                  id: "Los jugadores pueden registrar los resultados de sus partidos",
                })}
              />
              <RevaText
                color="text.secondary"
                id="Si activas esta opción, los jugadores podrán reportar los resultados de sus partidos en la app. De lo contrario, solo los organizadores podrán hacerlo."
              />
            </FormControl>
            ) : null}
            <FormControl>
              <FormControlLabel
                value={formData.requires_document_for_players}
                control={
                  <Checkbox
                    checked={formData.requires_document_for_players}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        requires_document_for_players: event.target.checked,
                      }))
                    }
                  />
                }
                label={intl.formatMessage({
                  id: "Registro con documento de identidad",
                })}
              />
              <RevaText
                color="text.secondary"
                id="Si activas esta opción, los jugadores deberán validar su identidad con Didit antes de inscribirse."
              />
            </FormControl>
          </Stack>
        </SectionContainer>

        <SectionContainer title={stepTitles[4]}>
          <Stack gap={3}>
            <Stack gap={3} direction="row">
              {isLeagueFormat ? (
                <Alert severity="info" sx={{ flex: 1 }} data-testid="league-entry-fee-hint">
                  {intl.formatMessage({ id: "league.wizard.entryFeeFromPriceList" })}
                </Alert>
              ) : (
                <FormControl>
                  <InputLabel htmlFor="outlined-adornment-amount">
                    {intl.formatMessage({ id: "Precio de inscripción" })}
                  </InputLabel>
                  <OutlinedInput
                    type="number"
                    id="outlined-adornment-amount"
                    value={formData.signup_price}
                    startAdornment={<InputAdornment position="start">{formData.currency_code}</InputAdornment>}
                    label={intl.formatMessage({ id: "Precio de inscripción" })}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        signup_price: e.target.value,
                      }))
                    }
                  />
                  <FormHelperText>
                    {intl.formatMessage({
                      id: "Si el torneo es gratuito, deja este campo vacío.",
                    })}
                  </FormHelperText>
                </FormControl>
              )}
              <FormControl sx={{ width: 150 }}>
                <CurrencySelector
                  value={formData.currency_code}
                  country={competition?.country || tournament?.competition?.data?.country}
                  onChange={(event, value) => setFormData((prev) => ({ ...prev, currency_code: value }))}
                  required
                />
              </FormControl>
            </Stack>
            {!isLeagueFormat ? (
            <FormControl>
              <FormControlLabel
                value={formData.show_bracket}
                control={
                  <Checkbox
                    checked={formData.show_bracket}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        show_bracket: event.target.checked,
                      }))
                    }
                  />
                }
                label={intl.formatMessage({
                  id: "Mostrar llave del torneo en la app",
                })}
              />
              <RevaText
                color="text.secondary"
                id="Si está activado, los jugadores podrán ver los partidos y el avance del torneo en la app."
              />
            </FormControl>
            ) : null}
          </Stack>
        </SectionContainer>
        {tournament && (
          <Accordion sx={{ p: 2, borderRadius: 2, m: "0 !important" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <RevaText id="⚠️ Acciones peligrosas" variant="h4" color="error" />
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <RevaText id="Reiniciar torneo" variant="subtitle1" color="text.secondary" />
                  <RevaText
                    id="Esto eliminará todos los resultados y estadísticas del torneo, pero mantendrá inscripciones y configuraciones."
                    color="text.secondary"
                  />
                  <Button variant="outlined" color="warning" sx={{ mt: 2 }} onClick={() => setRestartDialogOpen(true)}>
                    {intl.formatMessage({ id: "Reiniciar torneo" })}
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                  <RevaText id="Eliminar torneo" variant="subtitle1" color="text.secondary" />
                  <RevaText
                    id="Esta acción eliminará el torneo por completo, incluyendo participantes, estadísticas y resultados. Es irreversible."
                    color="text.secondary"
                  />
                  <Button variant="contained" color="error" sx={{ mt: 2 }} onClick={() => setDeleteDialogOpen(true)}>
                    {intl.formatMessage({ id: "Eliminar torneo" })}
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        <AlertDialog
          hideButton
          isOpen={restartDialogOpen}
          onConfirm={() => resetTournament()}
          onCancel={() => setRestartDialogOpen(false)}
          alertBody={intl.formatMessage({
            id: "¿Estás seguro de reiniciar este torneo?",
          })}
          confirmButtonTitle={intl.formatMessage({ id: "Reiniciar torneo" })}
          cancelButtonTitle={intl.formatMessage({ id: "Cancelar" })}
        />

        {/* Diálogo de confirmación para eliminar */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <WarningAmberIcon color="error" />
              <FormattedMessage id="Confirmar eliminación del torneo" />
            </Box>
          </DialogTitle>

          <DialogContent>
            <RevaText id="delete_tournament_confirmation" values={{ strong: (chunks) => <strong>{chunks}</strong> }} />
            <TextField
              fullWidth
              label={intl.formatMessage({
                id: "Escribe ELIMINAR para confirmar",
              })}
              value={confirmationInput}
              onChange={(e) => setConfirmationInput(e.target.value)}
              sx={{ mt: 2 }}
              autoFocus
            />
          </DialogContent>

          <DialogActions>
            <Button color="text.secondary" onClick={() => setDeleteDialogOpen(false)}>
              <FormattedMessage id="Cancelar" />
            </Button>
            <Button
              color="error"
              disabled={confirmationInput !== intl.formatMessage({ id: "ELIMINAR" })}
              onClick={confirmDeleteTournament}
            >
              <FormattedMessage id="Eliminar torneo" />
            </Button>
          </DialogActions>
        </Dialog>

        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", md: "flex-end" },
          }}
        >
          <CTAButton type="submit" loading={submitting} variant="contained" color="success">
            {intl.formatMessage({
              id: tournament ? "Guardar cambios" : "Crear Torneo",
            })}
          </CTAButton>
        </Box>
      </Stack>
    </form>
  );
}

export default CreateTournamentWizard;
